import { useState } from 'react';
import { SubFamily, SubFamilyCreate, SubFamilyUpdate } from '../types/costwand.d';
import { useSubFamilies } from '../hooks/useSubFamilies.ts';
import SubFamilyForm from '../components/SubFamilyForm.tsx';
import SubFamilyTable from '../components/SubFamilyTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  // DialogFooter, // Removed DialogFooter as it's not used for the form anymore
} from '../components/ui/dialog.tsx';
// import { useError } from '../context/ErrorContext.tsx'; // Not needed as apiClient handles global error

import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';

const SubFamiliesPage = () => {
  const {
    subFamilies,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createSubFamily,
    updateSubFamily,
    deleteSubFamily,
    fetchSubFamilies,
  } = useSubFamilies();

  // const { displayError } = useError(); // Not needed as apiClient handles global error

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingSubFamily, setEditingSubFamily] = useState<SubFamily | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  
  const { whitelabelConfig } = useWhitelabelContext();

  // Removed states for the separate error dialog for form submissions
  // const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  // const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(null);

  const getEntityTitle = (defaultTitle: string) => 
    whitelabelConfig.entities?.['subfamilies'].title || defaultTitle;

  const handleAddSubFamily = () => {
    setEditingSubFamily(null);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleEditSubFamily = (subFamily: SubFamily) => {
    setEditingSubFamily(subFamily);
    setIsFormOpen(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  const handleDeleteSubFamily = async (id: number) => {
    await deleteSubFamily(id);
    fetchSubFamilies(currentPage);
  };

  const handleFormSubmit = async (data: SubFamilyCreate | SubFamilyUpdate) => {
    setFormSubmitting(true);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);

    try {
      let success = false;
      if (editingSubFamily) {
        success = await updateSubFamily(editingSubFamily.id!, data as SubFamilyUpdate);
      } else {
        success = await createSubFamily(data as SubFamilyCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchSubFamilies(currentPage);
      } else {
        // The global error handler in apiClient would have already displayed the error.
        // We just need to ensure the form stays open if it failed.
        // displayError(error || "An unexpected error occurred during form submission."); // If we wanted to explicitly trigger global error here
        // setIsErrorDialogOpen(true); // Removed local error dialog trigger
      }
    } catch (e: any) {
      // The global error handler in apiClient would have already displayed the error.
      // displayError(e.message || "An unexpected error occurred."); // If we wanted to explicitly trigger global error here
      // setIsErrorDialogOpen(true); // Removed local error dialog trigger
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingSubFamily(null);
    // Removed clearing of local error messages
    // setErrorDialogMessage(null);
    // setIsErrorDialogOpen(false);
  };

  // Removed handleCloseErrorDialog function

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">{getEntityTitle('Subfamilies')} Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddSubFamily}>Add New {getEntityTitle('Subfamily', true)}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingSubFamily ? 'Edit ' + + getEntityTitle('subfamily', true) : 'Add New ' + getEntityTitle('subfamily', true)}</DialogTitle>
              <DialogDescription>
                {editingSubFamily ? 'Make changes to the  ' + getEntityTitle('subfamily', true) + ' here.' : 'Add a new ' + getEntityTitle('subfamily', true) + ' to your list.'}
              </DialogDescription>
            </DialogHeader>
            <SubFamilyForm
              initialData={editingSubFamily}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              entityId="subfamilies"
              // error={errorDialogMessage} // Removed the error prop
            />
          </DialogContent>
        </Dialog>
      </div>

      <SubFamilyTable
        subFamilies={subFamilies}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditSubFamily}
        onDelete={handleDeleteSubFamily}
        loading={loading}
        error={error}
        entityId="subfamilies"
        baseEndpoint="/subfamilies" // NEW: Pass the base endpoint
      />

      {/* Removed Error Dialog for form submission errors */}
      {/*
      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">Error</DialogTitle>
            <DialogDescription>
              An error occurred during the operation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-red-600">
            {errorDialogMessage || 'An unexpected error occurred.'}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseErrorDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
};

export default SubFamiliesPage;